/**
 * Sidebar icons.
 *
 * One per navigation item. Thirteen links of plain text is a list that gets read from
 * the top every time; with an icon each, the eye learns where "Turni" sits and goes
 * straight there. They are small and quiet on purpose — the label is still the thing
 * that says what the page is, and an icon that competes with it has failed.
 *
 * Drawn on a 20×20 grid at 1.6 stroke, matching the rest of the portal's icons.
 */

type IconProps = { className?: string };

const base = (className?: string) => ({
  viewBox: '0 0 20 20',
  'aria-hidden': true,
  className: className ?? 'w-4 h-4 shrink-0',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

/** Dashboard — the four panels of an overview. */
export const IconDashboard = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="2.5" width="6" height="6" rx="1" />
    <rect x="11.5" y="2.5" width="6" height="6" rx="1" />
    <rect x="2.5" y="11.5" width="6" height="6" rx="1" />
    <rect x="11.5" y="11.5" width="6" height="6" rx="1" />
  </svg>
);

/** Shifts — a clock, because a shift is hours. */
export const IconShifts = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="10" cy="10" r="7.5" />
    <path d="M10 5.5V10l3 2" />
  </svg>
);

/** Assignments — a person pinned to a place. */
export const IconAssignments = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="7.5" cy="6" r="2.75" />
    <path d="M2.5 16.5c0-2.8 2.24-4.5 5-4.5 1.02 0 1.96.23 2.75.65" />
    <path d="M15 18c2-2.4 3-4.06 3-5.5a3 3 0 1 0-6 0c0 1.44 1 3.1 3 5.5Z" />
  </svg>
);

/** Hours and pay — a banknote. */
export const IconPayroll = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2" y="5" width="16" height="10" rx="1.5" />
    <circle cx="10" cy="10" r="2.25" />
    <path d="M5 8v4M15 8v4" />
  </svg>
);

/** Employees — two people. */
export const IconEmployees = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="7.5" cy="6.5" r="2.75" />
    <path d="M2.5 16.5c0-2.8 2.24-4.75 5-4.75s5 1.95 5 4.75" />
    <path d="M13.5 4.2a2.75 2.75 0 0 1 0 5.3M14.5 12.2c1.9.5 3 2.1 3 4.3" />
  </svg>
);

/** Sites — a map pin. */
export const IconSites = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 18c3.4-4 5.2-6.86 5.2-9a5.2 5.2 0 1 0-10.4 0c0 2.14 1.8 5 5.2 9Z" />
    <circle cx="10" cy="8.8" r="1.9" />
  </svg>
);

/** Jobs — a work case. */
export const IconJobs = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="6" width="15" height="10.5" rx="1.5" />
    <path d="M7.25 6V4.75A1.25 1.25 0 0 1 8.5 3.5h3a1.25 1.25 0 0 1 1.25 1.25V6" />
    <path d="M2.5 10.5h15" />
  </svg>
);

/** Expiries — a clock with a warning, because these are deadlines that bite. */
export const IconAlerts = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M10 2.75 18 16.5H2L10 2.75Z" />
    <path d="M10 8v3.25M10 13.75v.5" />
  </svg>
);

/** Required documents — a checklist. */
export const IconCompliance = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M4 3.5h12v13H4z" />
    <path d="m6.75 8.5 1.5 1.5 3-3.25M6.75 13.5h6.5" />
  </svg>
);

/** Document library — stacked papers. */
export const IconDocuments = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M11 2.5H5.5v15h9V6l-3.5-3.5Z" />
    <path d="M11 2.5V6h3.5" />
  </svg>
);

/** Activity log — a list with a clock. */
export const IconAudit = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M3 5h9M3 9h6M3 13h5" />
    <circle cx="14" cy="13" r="4" />
    <path d="M14 11.3V13l1.2.9" />
  </svg>
);

/** Recycle bin — a waste basket. */
export const IconRecycleBin = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M3.5 5.5h13M8 5.5V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5" />
    <path d="M5 5.5 5.8 16a1.5 1.5 0 0 0 1.5 1.4h5.4A1.5 1.5 0 0 0 14.2 16L15 5.5" />
    <path d="M8.5 9v5M11.5 9v5" />
  </svg>
);

/** Inventory — a crate of kit. */
export const IconInventory = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M2.5 6.5 10 3l7.5 3.5v7L10 17l-7.5-3.5Z" />
    <path d="M2.5 6.5 10 10l7.5-3.5M10 10v7" />
  </svg>
);

/** Vehicles — a van, side on. */
export const IconVehicles = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <path d="M2.5 13.5v-7a1 1 0 0 1 1-1h8v8.5M11.5 8h3l2.5 3v2.5h-1.5" />
    <path d="M2.5 13.5h1.5M8 13.5h4" />
    <circle cx="6" cy="14" r="1.75" />
    <circle cx="14" cy="14" r="1.75" />
  </svg>
);

/** Messages — an envelope. */
export const IconMessages = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="4.5" width="15" height="11" rx="1.5" />
    <path d="m3 5.5 7 5.5 7-5.5" />
  </svg>
);

/** Calendar — a month page with its binding rings and one day marked. */
export const IconCalendar = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <rect x="2.5" y="4" width="15" height="13.5" rx="1.5" />
    <path d="M2.5 8h15M6.5 2.5v3M13.5 2.5v3" />
    <circle cx="7" cy="12" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="10" cy="12" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="13" cy="12" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

/** Leave and permits — a sun, for time away. */
export const IconLeave = ({ className }: IconProps) => (
  <svg {...base(className)}>
    <circle cx="10" cy="10" r="3.5" />
    <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" />
  </svg>
);
